import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import DynamicTable from "../common/DynamicTable";
import { useTranslation } from "react-i18next";
import DeletedItemsTable from "../common/DeletedItemsTable";
import { useUser } from "../../pages/login/UserContextPersistent";
import axios from "axios";
import config from "../../config";
import {
  getUserDateFormat,
  convertToDateFnsFormat,
  formatDate,
} from "../../utils/formatDate";
import { useEffect } from "react";

const headers = ["recentlyDeletedItems", "date", "recover"];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function DeleteData() {
  const { t } = useTranslation();
  const { state } = useUser();
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Get user data from context
  const userId = state?.user?.id;
  const userEmail = state?.user?.email;
  const organizationId = state?.user?.organizationId;

  // Map modules to their required API paths
  const modulePermissions = {
    vehicles: "/api/vehicle",
    routes: "/api/route",
    stops: "/api/stop",
    alerts: "/api/alert",
    schedule: "/api/schedule",
    driver: "/api/driver",
    sites: "/api/site",
    users: "/api/user",
    managedata: "/api/managedata/reports",
  };

  // Function to check if user has permission for a module
  const hasPermission = (requiredPath) => {
    if (!state?.features || !requiredPath) {
      return true;
    }
    return state.features.some((feature) =>
      feature.api_path.includes(requiredPath)
    );
  };

  // Filter modules based on user permissions
  const availableModules = useMemo(() => {
    return Object.keys(modulePermissions).filter((module) =>
      hasPermission(modulePermissions[module])
    );
  }, [state?.features]);

  const durations = ["today", "yesterday", "thisWeek", "thisMonth", "custom"];

  const handleModuleChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedModules(typeof value === "string" ? value.split(",") : value);
  };

  const calculateDateRange = (duration) => {
    const today = new Date();
    let start, end;

    switch (duration) {
      case "today":
        start = new Date(today.setHours(0, 0, 0, 0));
        end = new Date(today.setHours(23, 59, 59, 999));
        break;
      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        start = new Date(yesterday.setHours(0, 0, 0, 0));
        end = new Date(yesterday.setHours(23, 59, 59, 999));
        break;
      case "thisWeek":
        const firstDayOfWeek = new Date(today);
        firstDayOfWeek.setDate(today.getDate() - today.getDay());
        start = new Date(firstDayOfWeek.setHours(0, 0, 0, 0));
        end = new Date(today.setHours(23, 59, 59, 999));
        break;
      case "thisMonth":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.setHours(23, 59, 59, 999));
        break;
      default:
        return { startDate: null, endDate: null };
    }

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  };

  const handleDelete = async () => {
    try {
      let dateRange;
      const dateFormat = getUserDateFormat();

      if (selectedDuration === "custom") {
        const startDateISO = startDate
          ? new Date(startDate).toISOString()
          : null;
        const endDateISO = endDate ? new Date(endDate).toISOString() : null;

        dateRange = {
          startDate: startDateISO,
          endDate: endDateISO,
          // Use formatDate utility for display/logging
          startDateFormatted: startDate ? formatDate(startDate) : "",
          endDateFormatted: endDate ? formatDate(endDate) : "",
        };
      } else {
        const calculatedRange = calculateDateRange(selectedDuration);
        dateRange = {
          startDate: calculatedRange.startDate,
          endDate: calculatedRange.endDate,
          startDateFormatted: formatDate(calculatedRange.startDate),
          endDateFormatted: formatDate(calculatedRange.endDate),
        };
      }

      const payload = {
        moduleNames: selectedModules,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        organizationId: organizationId,
        userId: userId,
        email: userEmail,
      };

      console.log("Delete API Payload:", payload);
      console.log(
        `Date Range: ${dateRange.startDateFormatted} to ${dateRange.endDateFormatted}`
      );

      const response = await axios.post(
        `${config.baseURL}/email/data-delete-request`,
        payload,
        { withCredentials: true }
      );

      if (response.status === 200) {
        // Handle success - show success message
        console.log("Data delete request sent successfully");
        setSuccessMessage("Request sent successfully!");
        // Reset form
        setSelectedModules([]);
        setSelectedDuration("");
        setStartDate(null);
        setEndDate(null);

        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage("");
        }, 3000);
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      // Handle error - show error message
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          position: "relative",
          gap: { xs: "15px", sm: "20px" },
          borderRadius: { xs: "8px", sm: "10px" },
          margin: {
            xs: "10px",
            sm: "20px 40px",
            md: "20px 40px",
          },
          background: "#FFFFFF",
          border: "1px solid #A5DCF3",
          boxShadow: "-2px 2px 5px 0px rgba(0,0,0,0.25)",
          padding: {
            xs: "12px",
            sm: "15px",
            md: "20px",
          },
        }}
      >
        <Box
          sx={{
            background: "#647490",
            boxShadow: "-2px 2px 5px 0px rgba(0,0,0,0.25)",
            color: "#FFFFFF",
            fontFamily: "Roboto",
            borderRadius: { xs: "6px", sm: "8px" },
            fontWeight: 600,
            fontSize: {
              xs: "18px",
              sm: "20px",
              md: "24px",
            },
            lineHeight: "100%",
            padding: {
              xs: "10px",
              sm: "12px",
              md: "12px",
            },
            marginBottom: { xs: "15px", sm: "20px" },
            textAlign: "left",
          }}
        >
          {t("Data Removal Request")}
        </Box>

        <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
          <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box>
                <InputLabel
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: {
                      xs: "16px",
                      sm: "18px",
                      md: "20px",
                    },
                    lineHeight: "100%",
                    color: "#1F242E",
                    mb: { xs: 0.5, sm: 1 },
                  }}
                >
                  {t("module")}
                </InputLabel>
                <FormControl fullWidth>
                  <Select
                    multiple
                    displayEmpty
                    value={selectedModules}
                    onChange={handleModuleChange}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return t("Choose modules to delete");
                      }
                      return selected.map((s) => t(s)).join(", ");
                    }}
                    MenuProps={MenuProps}
                    sx={{
                      width: "100%",
                      height: { xs: "45px", sm: "50px", md: "55px" },
                      borderRadius: { xs: "12px", sm: "16px" },
                      padding: { xs: "12px 20px", sm: "16px 24px" },
                      border: "2px solid #183059",
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&.Mui-focused": {
                        border: "2px solid #183059",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                      },
                    }}
                  >
                    <MenuItem disabled value="">
                      <em>{t("Choose modules to delete")}</em>
                    </MenuItem>
                    {availableModules.map((module) => (
                      <MenuItem key={module} value={module}>
                        <Checkbox
                          checked={selectedModules.indexOf(module) > -1}
                        />
                        <ListItemText primary={t(module)} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box>
                <InputLabel
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: {
                      xs: "16px",
                      sm: "18px",
                      md: "20px",
                    },
                    lineHeight: "100%",
                    color: "#1F242E",
                    mb: { xs: 0.5, sm: 1 },
                  }}
                >
                  {t("duration")}
                </InputLabel>
                <FormControl fullWidth>
                  <Select
                    displayEmpty
                    value={selectedDuration}
                    onChange={(e) => setSelectedDuration(e.target.value)}
                    sx={{
                      width: "100%",
                      height: { xs: "45px", sm: "50px", md: "55px" },
                      borderRadius: { xs: "12px", sm: "16px" },
                      padding: { xs: "12px 20px", sm: "16px 24px" },
                      border: "2px solid #183059",
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&.Mui-focused": {
                        border: "2px solid #183059",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                      },
                    }}
                  >
                    <MenuItem value="">{t("Choose time period")}</MenuItem>
                    {durations.map((duration) => (
                      <MenuItem key={duration} value={duration}>
                        {t(duration)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={2}
              sx={{
                display: "flex",
                justifyContent: { xs: "stretch", md: "flex-end" },
              }}
            >
              <Button
                onClick={handleDelete}
                disabled={
                  selectedModules.length === 0 ||
                  !selectedDuration ||
                  (selectedDuration === "custom" && (!startDate || !endDate))
                }
                sx={{
                  width: { xs: "100%", md: "250px" },
                  height: { xs: "40px", sm: "50px", md: "55px" },
                  borderRadius: { xs: "6px", sm: "8px" },
                  padding: { xs: "8px 20px", sm: "16px 24px" },
                  background: "#EE3B2B",
                  border: "1px solid #414958",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  textTransform: "none",
                  marginTop: "30px",
                  fontSize: {
                    xs: "14px",
                    sm: "16px",
                  },
                  "&:hover": {
                    background: "#cb2819ff",
                    borderColor: "#2A3140",
                  },
                  "&:disabled": {
                    color: "#FFFFFF",
                  },
                }}
              >
                {t("Send Request")}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Success Message */}
        {successMessage && (
          <Box
            sx={{
              mt: 2,
              padding: "12px 16px",
              color: "#4CAF50",
              borderRadius: "8px",
              fontFamily: "Roboto",
              fontSize: "16px",
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            {successMessage}
          </Box>
        )}

        {/* Custom Date Range Fields */}
        {selectedDuration === "custom" && (
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={6}>
                <InputLabel
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: {
                      xs: "16px",
                      sm: "18px",
                      md: "20px",
                    },
                    lineHeight: "100%",
                    color: "#1F242E",
                    mb: { xs: 0.5, sm: 1 },
                  }}
                >
                  {t("startDate")}
                </InputLabel>
                <DatePicker
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format={convertToDateFnsFormat(getUserDateFormat())}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          height: { xs: "45px", sm: "50px", md: "55px" },
                          borderRadius: { xs: "12px", sm: "16px" },
                          border: "2px solid #183059",
                          "& fieldset": {
                            border: "none",
                          },
                          "&:hover fieldset": {
                            border: "none",
                          },
                          "&.Mui-focused fieldset": {
                            border: "none",
                          },
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InputLabel
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: {
                      xs: "16px",
                      sm: "18px",
                      md: "20px",
                    },
                    lineHeight: "100%",
                    color: "#1F242E",
                    mb: { xs: 0.5, sm: 1 },
                  }}
                >
                  {t("endDate")}
                </InputLabel>
                <DatePicker
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format={convertToDateFnsFormat(getUserDateFormat())}
                  minDate={startDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          height: { xs: "45px", sm: "50px", md: "55px" },
                          borderRadius: { xs: "12px", sm: "16px" },
                          border: "2px solid #183059",
                          "& fieldset": {
                            border: "none",
                          },
                          "&:hover fieldset": {
                            border: "none",
                          },
                          "&.Mui-focused fieldset": {
                            border: "none",
                          },
                        },
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* <Box sx={{ mt: 3 }}>
                    <DeletedItemsTable headers={headers} />
                </Box> */}
      </Box>
    </LocalizationProvider>
  );
}
